import { OnlineUserSchema, THeartbeatResponseSchema, TUserOnline, UserOnlineNamespace } from '@onesource/schemas';
import { Collection } from '@signaldb/core';
import maverickReactivityAdapter from '@signaldb/maverickjs';
import { match, P } from 'ts-pattern';
import { array } from 'zod';

const DEFAULT_DISCONNECT_TIMEOUT_MS = 3 * 60 * 1000;
const MIN_HEARTBEAT_INTERVAL_MS = 5 * 1000;
const HEARTBEAT_SAFETY_MARGIN_MS = 10 * 1000;

export class WhoIsOnlineCollection extends Collection<TUserOnline> {
  private total = 0;
  private disconnectTimeoutMs = DEFAULT_DISCONNECT_TIMEOUT_MS;

  constructor() {
    super({
      name: UserOnlineNamespace,
      memory: [],
      reactivity: maverickReactivityAdapter,
    });
  }

  updateFromHeartbeat(response: THeartbeatResponseSchema) {
    const { users, totalUsers, disconnectTimeoutMs } = response;

    this.total = totalUsers;
    this.disconnectTimeoutMs = disconnectTimeoutMs;

    const parsed = array(OnlineUserSchema, { invalid_type_error: 'users must be an array' }).safeParse(users);

    if (!parsed.success) {
      console.error('updateFromHeartbeat parse error', parsed.error);
      return;
    }

    this.removeMany({});
    if (parsed.data.length > 0) {
      this.insertMany(parsed.data);
    }
  }

  /**
   * Recommended refetch interval — fires shortly before the server-side disconnect timeout.
   */
  getHeartbeatInterval(): number {
    return Math.max(this.disconnectTimeoutMs - HEARTBEAT_SAFETY_MARGIN_MS, MIN_HEARTBEAT_INTERVAL_MS);
  }

  findUsers(payload: { page: number; perPage: number; currentUserId?: string }) {
    const { page, perPage = 6, currentUserId } = payload;
    const skip = match(Math.max(page * perPage, 0))
      .with(P.number.gte(this.total), s => s - perPage)
      .otherwise(s => s);

    return this.find(
      { id: { $not: { $eq: currentUserId } } },
      { sort: { isActive: -1 }, skip, limit: perPage },
    ).fetch();
  }

  findAllUsers(currentUserId?: string) {
    return this.find({ id: { $not: { $eq: currentUserId } } }, { sort: { isActive: -1 } }).fetch();
  }

  totalUsers(currentUserId?: string) {
    return this.find({ id: { $not: { $eq: currentUserId } } }, { limit: this.total }).count();
  }

  clear() {
    this.removeMany({});
    this.total = 0;
  }
}

export const WhoIsOnline = new WhoIsOnlineCollection();
